import type { DeviceType, IconPackId } from '@ukrop-nm/shared';
import {
  Camera,
  Cloud,
  Cpu,
  HardDrive,
  Laptop,
  Monitor,
  Printer,
  Router,
  Server,
  Shield,
  Smartphone,
  Tablet,
  Wifi,
  HelpCircle,
  Box,
  Satellite,
  Antenna,
  Zap,
  Radio,
} from 'lucide-react';
import type { ComponentType, ReactNode, SVGProps } from 'react';

type SvgProps = SVGProps<SVGSVGElement> & { size?: number };

function Frame({
  children,
  pack,
  size,
}: {
  children: ReactNode;
  pack: IconPackId;
  size: number;
}) {
  if (pack === 'classic_network') {
    return (
      <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden>
        <rect x="1" y="1" width="30" height="30" rx="2" fill="#036897" />
        <g transform="translate(4 4) scale(0.75)" stroke="#fff" fill="none" strokeWidth="2">
          {children}
        </g>
      </svg>
    );
  }
  if (pack === 'flat_cloud') {
    return (
      <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden>
        <rect x="1" y="1" width="30" height="30" rx="6" fill="#0078D4" />
        <g transform="translate(5 5) scale(0.7)" stroke="#fff" fill="none" strokeWidth="2">
          {children}
        </g>
      </svg>
    );
  }
  if (pack === 'minimal') {
    return (
      <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden>
        <rect x="2" y="2" width="28" height="28" rx="8" fill="#f5f5f7" stroke="#1d1d1f" strokeWidth="1" />
        <g transform="translate(6 6) scale(0.625)" stroke="#1d1d1f" fill="none" strokeWidth="1.8">
          {children}
        </g>
      </svg>
    );
  }
  // ukrop default: just the glyph
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden>
      <g stroke="currentColor" strokeWidth="2">
        {children}
      </g>
    </svg>
  );
}

function glyph(type: DeviceType): ReactNode {
  switch (type) {
    case 'switch':
      return (
        <>
          <rect x="3" y="10" width="26" height="12" rx="2" />
          <circle cx="8" cy="16" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="13" cy="16" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="18" cy="16" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="23" cy="16" r="1.5" fill="currentColor" stroke="none" />
        </>
      );
    case 'router':
      return (
        <>
          <rect x="6" y="12" width="20" height="10" rx="2" />
          <path d="M10 12V8M16 12V6M22 12V8" strokeLinecap="round" />
        </>
      );
    case 'wifi_router':
      return (
        <>
          <rect x="7" y="14" width="18" height="9" rx="2" />
          <path d="M11 12c2.5-2.5 7.5-2.5 10 0M13 10c1.8-1.5 4.2-1.5 6 0" strokeLinecap="round" />
        </>
      );
    case 'access_point':
      return (
        <>
          <circle cx="16" cy="18" r="6" />
          <path d="M16 12V9" strokeLinecap="round" />
          <path d="M10 8c3.5-3.5 8.5-3.5 12 0M12 5.5c2.5-2.2 5.5-2.2 8 0" strokeLinecap="round" />
        </>
      );
    case 'firewall':
      return (
        <>
          <path d="M16 4l10 4v6c0 7-4.5 11-10 13C10.5 25 6 21 6 14V8l10-4z" />
          <path d="M11 16h10" />
        </>
      );
    case 'server':
      return (
        <>
          <rect x="6" y="5" width="20" height="8" rx="1.5" />
          <rect x="6" y="15" width="20" height="8" rx="1.5" />
          <circle cx="10" cy="9" r="1" fill="currentColor" stroke="none" />
          <circle cx="10" cy="19" r="1" fill="currentColor" stroke="none" />
        </>
      );
    case 'desktop':
      return (
        <>
          <rect x="5" y="6" width="22" height="14" rx="1.5" />
          <path d="M12 24h8M16 20v4" strokeLinecap="round" />
        </>
      );
    case 'laptop':
      return (
        <>
          <rect x="7" y="8" width="18" height="12" rx="1.5" />
          <path d="M4 22h24" strokeLinecap="round" />
        </>
      );
    case 'tablet':
      return (
        <>
          <rect x="9" y="5" width="14" height="22" rx="2" />
          <circle cx="16" cy="24" r="1" fill="currentColor" stroke="none" />
        </>
      );
    case 'display':
      return (
        <>
          <rect x="4" y="7" width="24" height="14" rx="2" />
          <path d="M12 25h8M16 21v4" strokeLinecap="round" />
        </>
      );
    case 'ip_phone':
      /* Desk IP-phone (Grandstream-like): cradle handset + LCD + keypad */
      return (
        <>
          <path
            d="M7 8c0-1.5 1-2.5 2.5-2.5h2c1 0 1.5.8 1.5 1.8V10H9.5C8 10 7 9.2 7 8z"
            strokeLinejoin="round"
          />
          <path d="M9 10v2.5" strokeLinecap="round" />
          <rect x="11" y="8" width="14" height="18" rx="2" />
          <rect x="13.5" y="10" width="9" height="5" rx="0.8" />
          <circle cx="14.5" cy="18.5" r="0.9" fill="currentColor" stroke="none" />
          <circle cx="18" cy="18.5" r="0.9" fill="currentColor" stroke="none" />
          <circle cx="21.5" cy="18.5" r="0.9" fill="currentColor" stroke="none" />
          <circle cx="14.5" cy="21.5" r="0.9" fill="currentColor" stroke="none" />
          <circle cx="18" cy="21.5" r="0.9" fill="currentColor" stroke="none" />
          <circle cx="21.5" cy="21.5" r="0.9" fill="currentColor" stroke="none" />
        </>
      );
    case 'wifi_phone':
      return (
        <>
          <rect x="11" y="5" width="10" height="22" rx="2" />
          <path d="M14 10c1.2-1 2.8-1 4 0" strokeLinecap="round" />
        </>
      );
    case 'printer':
      return (
        <>
          <path d="M9 8V5h14v3" />
          <rect x="6" y="12" width="20" height="10" rx="1.5" />
          <rect x="10" y="18" width="12" height="6" />
        </>
      );
    case 'camera':
      return (
        <>
          <rect x="5" y="10" width="16" height="12" rx="2" />
          <path d="M21 13l5-3v12l-5-3" />
          <circle cx="13" cy="16" r="2.5" />
        </>
      );
    case 'nas':
      return (
        <>
          <rect x="7" y="6" width="18" height="20" rx="2" />
          <path d="M10 11h12M10 16h12M10 21h8" />
        </>
      );
    case 'modem':
      return (
        <>
          <rect x="5" y="12" width="22" height="10" rx="2" />
          <circle cx="10" cy="17" r="1.5" fill="currentColor" stroke="none" />
          <circle cx="16" cy="17" r="1.5" fill="currentColor" stroke="none" />
          <path d="M22 8v4" strokeLinecap="round" />
        </>
      );
    case 'satellite':
      return (
        <>
          <path d="M8 20c4-8 12-8 16 0" />
          <path d="M11 20c2.5-4 7.5-4 10 0" />
          <circle cx="16" cy="20" r="1.5" fill="currentColor" stroke="none" />
          <path d="M20 8l4 4M22 6l4 4" />
        </>
      );
    case 'lte_antenna':
      return (
        <>
          <path d="M16 28V16" strokeLinecap="round" />
          <path d="M6 16c3.5-9 16.5-9 20 0" />
          <path d="M9.5 16c2.5-5.5 10.5-5.5 13 0" />
          <circle cx="16" cy="16" r="1.4" fill="currentColor" stroke="none" />
          <path d="M16 7V4" strokeLinecap="round" />
        </>
      );
    case 'cloud':
      return (
        <>
          <path d="M10 20h12a5 5 0 0 0 0-10 7 7 0 0 0-13-2 5 5 0 0 0 1 12z" />
        </>
      );
    case 'iot':
      return (
        <>
          <circle cx="16" cy="16" r="3" />
          <path d="M16 6v3M16 23v3M6 16h3M23 16h3M9 9l2 2M21 21l2 2M21 9l-2 2M9 21l-2 2" />
        </>
      );
    case 'ups':
      return (
        <>
          <rect x="8" y="6" width="16" height="20" rx="2" />
          <path d="M14 12h4l-2 4h4" strokeLinecap="round" strokeLinejoin="round" />
        </>
      );
    case 'power_station':
      // Portable power station / EcoFlow-like front panel (screen + AC outlets)
      return (
        <>
          <rect x="6" y="4.5" width="20" height="23" rx="2.5" />
          <rect x="11" y="7" width="10" height="4.5" rx="0.8" />
          <circle cx="16" cy="14" r="1.15" />
          <path d="M16 12.7v1.3" strokeLinecap="round" />
          <rect x="8" y="8" width="2" height="1.3" rx="0.25" />
          <rect x="8" y="10" width="2" height="1.3" rx="0.25" />
          <circle cx="23.2" cy="9" r="1.25" />
          <circle cx="22.2" cy="12" r="0.65" />
          <circle cx="24.2" cy="12" r="0.65" />
          <circle cx="11" cy="20.5" r="2.15" />
          <circle cx="16" cy="20.5" r="2.15" />
          <circle cx="21" cy="20.5" r="2.15" />
          <path d="M10.3 19.7v1.6M11.7 19.7v1.6M15.3 19.7v1.6M16.7 19.7v1.6M20.3 19.7v1.6M21.7 19.7v1.6" strokeLinecap="round" />
          <path d="M8.5 27.5h3M20.5 27.5h3" strokeLinecap="round" />
        </>
      );
    case 'controller':
      return (
        <>
          <rect x="5" y="8" width="22" height="16" rx="2" />
          <circle cx="12" cy="16" r="2" />
          <path d="M18 13h4M18 16h4M18 19h3" />
        </>
      );
    case 'zone':
      return <rect x="4" y="6" width="24" height="20" rx="2" strokeDasharray="3 2" />;
    default:
      return (
        <>
          <rect x="6" y="6" width="20" height="20" rx="3" />
          <path d="M11 16h10M16 11v10" />
        </>
      );
  }
}

const LUCIDE_MAP: Partial<Record<DeviceType, ComponentType<SvgProps>>> = {
  switch: Radio,
  router: Router,
  wifi_router: Wifi,
  access_point: Radio,
  firewall: Shield,
  server: Server,
  desktop: Monitor,
  laptop: Laptop,
  tablet: Tablet,
  display: Monitor,
  // ip_phone: custom Grandstream-like glyph below (not Lucide Phone)
  wifi_phone: Smartphone,
  printer: Printer,
  camera: Camera,
  nas: HardDrive,
  modem: Cpu,
  lte_antenna: Antenna,
  satellite: Satellite,
  cloud: Cloud,
  iot: Cpu,
  ups: Zap,
  controller: Box,
  zone: Box,
  generic: HelpCircle,
};

function PowerStationIcon({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden>
      <g stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
        <rect x="6" y="4.5" width="20" height="23" rx="2.5" />
        <rect x="11" y="7" width="10" height="4.5" rx="0.8" />
        <circle cx="16" cy="14" r="1.15" />
        <path d="M16 12.7v1.3" strokeLinecap="round" />
        <rect x="8" y="8" width="2" height="1.3" rx="0.25" />
        <rect x="8" y="10" width="2" height="1.3" rx="0.25" />
        <circle cx="23.2" cy="9" r="1.25" />
        <circle cx="22.2" cy="12" r="0.65" />
        <circle cx="24.2" cy="12" r="0.65" />
        <circle cx="11" cy="20.5" r="2.15" />
        <circle cx="16" cy="20.5" r="2.15" />
        <circle cx="21" cy="20.5" r="2.15" />
        <path
          d="M10.3 19.7v1.6M11.7 19.7v1.6M15.3 19.7v1.6M16.7 19.7v1.6M20.3 19.7v1.6M21.7 19.7v1.6"
          strokeLinecap="round"
        />
        <path d="M8.5 27.5h3M20.5 27.5h3" strokeLinecap="round" />
      </g>
    </svg>
  );
}

function GrandstreamPhoneIcon({ size = 28 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden>
      <g stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
        <path d="M6 7.5c0-1.4.9-2.3 2.3-2.3h2.2c1 0 1.5.7 1.5 1.7V10H8.5C7.1 10 6 9.1 6 7.5z" />
        <path d="M8.5 10v2" strokeLinecap="round" />
        <rect x="11" y="7" width="15" height="19" rx="2.2" />
        <rect x="13.5" y="9.5" width="10" height="5.5" rx="1" />
        <circle cx="15" cy="18.5" r="1" fill="currentColor" stroke="none" />
        <circle cx="18.5" cy="18.5" r="1" fill="currentColor" stroke="none" />
        <circle cx="22" cy="18.5" r="1" fill="currentColor" stroke="none" />
        <circle cx="15" cy="22" r="1" fill="currentColor" stroke="none" />
        <circle cx="18.5" cy="22" r="1" fill="currentColor" stroke="none" />
        <circle cx="22" cy="22" r="1" fill="currentColor" stroke="none" />
      </g>
    </svg>
  );
}

export function DeviceIcon({
  type,
  pack = 'ukrop',
  size = 28,
}: {
  type: DeviceType;
  pack?: IconPackId;
  size?: number;
}) {
  if (pack === 'outline') {
    if (type === 'ip_phone') return <GrandstreamPhoneIcon size={size} />;
    if (type === 'power_station') return <PowerStationIcon size={size} />;
    if (type === 'access_point' || type === 'lte_antenna') {
      return (
        <svg width={size} height={size} viewBox="0 0 32 32" fill="none" aria-hidden>
          <g stroke="currentColor" strokeWidth="1.75" strokeLinejoin="round">
            {glyph(type)}
          </g>
        </svg>
      );
    }
    const Icon = LUCIDE_MAP[type] ?? HelpCircle;
    return <Icon size={size} strokeWidth={1.75} aria-hidden />;
  }

  return (
    <Frame pack={pack} size={size}>
      {glyph(type)}
    </Frame>
  );
}

export function StatusPattern({ status }: { status: string }) {
  const symbol =
    status === 'online'
      ? '●'
      : status === 'degraded'
        ? '／／'
        : status === 'offline'
          ? '✕'
          : status === 'inactive'
            ? '○'
            : status === 'sleeping'
              ? '☾'
              : status === 'maintenance'
                ? '🔧'
                : status === 'passive'
                  ? '◌'
                  : status === 'stale'
                    ? '◷'
                    : status === 'site_unreachable'
                      ? '✘✘'
                      : '?';
  return <span aria-label={status}>{symbol}</span>;
}
